import {
    ApproveAccrualReviewBody,
    LineItemAllocation,
} from "@common/schemas/domains/accrualReview/accrual-review.schema";
import { MatchableItem, MatchableTransaction, MatchItemType } from "../types";
import { parseTransactionMatchId } from "../mappers";
import { toLineItemAllocations } from "./allocationHelpers";
import {
    buildRemainingAccountOverrides,
    RemainingLine,
} from "./useRemainingLines";

interface Params {
    sourceTransaction: Pick<MatchableTransaction, "id" | "categoryId">;
    selectedItems: MatchableItem[];
    paymentAmounts: Record<string, number>;
    remainingLines: RemainingLine[];
    primaryAccrualLedgerId: string | undefined;
    primaryCashLedgerId: string | undefined;
}

export function buildAllocationBody({
    sourceTransaction,
    selectedItems,
    paymentAmounts,
    remainingLines,
    primaryAccrualLedgerId,
    primaryCashLedgerId,
}: Params): ApproveAccrualReviewBody {
    const transactionMatch = selectedItems.find(
        (item) => item.type === MatchItemType.TRANSACTION,
    );
    const lineItemAllocations: LineItemAllocation[] = toLineItemAllocations(
        selectedItems,
        paymentAmounts,
    );
    const remainingAllocations = remainingLines.flatMap((line) => {
        if (
            line.amount <= 0 ||
            !line.accrualAccountId ||
            !primaryAccrualLedgerId
        ) {
            return [];
        }
        return [
            {
                amount: line.amount,
                accountOverrides: buildRemainingAccountOverrides({
                    accrualLedgerId: primaryAccrualLedgerId,
                    accrualAccountId: line.accrualAccountId,
                    cashLedgerId: primaryCashLedgerId,
                    cashAccountId: line.cashAccountId,
                }),
                ...(line.classIds.length > 0 && { classIds: line.classIds }),
                ...(line.description && { description: line.description }),
                ...(line.categoryId &&
                    line.categoryId !== sourceTransaction.categoryId && {
                        categoryId: line.categoryId,
                    }),
            },
        ];
    });

    return {
        transactionId: sourceTransaction.id,
        ...(transactionMatch && {
            matchedTransactionId: parseTransactionMatchId(transactionMatch.id),
        }),
        ...(lineItemAllocations.length > 0 && { lineItemAllocations }),
        ...(remainingAllocations.length > 0 && {
            remaining: remainingAllocations,
        }),
    };
}
